import { offlineStorageService } from '@/services/offline/OfflineStorageService';
import { geolocationService } from '@/services/geolocation/GeolocationService';

export interface MapLocation {
  lat: number;
  lng: number;
}

export interface MapPoint {
  id: string;
  lat: number;
  lng: number;
  name: string;
  radius: number;
  cachedAt: number;
  tiles: string[];
}

export interface AddressData {
  road?: string;
  house_number?: string;
  suburb?: string;
  city?: string;
  state?: string;
  postcode?: string;
  country?: string;
  [key: string]: string | number | undefined;
}

// Lightweight representation of an address entry stored offline
export interface AddressEntry {
  id: string;
  lat: number;
  lng: number;
  address: AddressData;
  cachedAt: number;
  searchTerms: string[];
}

export interface LocalityPoint {
  id: string;
  lat: number;
  lng: number;
  name: string;
  radius: number;
  cachedAt: number;
  expiresAt: number;
  address?: AddressData;
}

export interface MapCacheSettings {
  cacheRadius: number;
  cacheExpiration: number;
  autoCache: boolean;
  maxCachePoints: number;
  addressCacheRadius: number;
}

export interface MapCacheStatus {
  cachedPointsCount: number;
  cachedAddressesCount: number;
  totalVisits: number;
  pendingSync: number;
  lastCacheUpdate: number | null;
  hasMapCache: boolean;
  hasAddressCache: boolean;
}

class MapCacheService {
  // Default settings are now managed by OfflineStorageService

  /**
   * Get cache settings
   */
  getSettings(): MapCacheSettings {
    return offlineStorageService.getMapSettings();
  }

  /**
   * Save cache settings
   */
  saveSettings(settings: Partial<MapCacheSettings>): void {
    offlineStorageService.saveMapSettings(settings);
  }

  /**
   * Get cache status
   */
  getCacheStatus(): MapCacheStatus {
    const cachedPoints = offlineStorageService.getCachedPoints();
    const cachedAddresses = offlineStorageService.getCachedAddresses();
    const offlineVisits = offlineStorageService.getOfflineVisits();
    const pendingSync = offlineVisits.filter(v => !v.synced).length;
    
    return {
      cachedPointsCount: cachedPoints.length,
      cachedAddressesCount: cachedAddresses.length,
      totalVisits: offlineVisits.length,
      pendingSync: pendingSync,
      lastCacheUpdate: cachedPoints.length > 0 
        ? Math.max(...cachedPoints.map(p => p.cachedAt)) 
        : null,
      hasMapCache: cachedPoints.length > 0,
      hasAddressCache: cachedAddresses.length > 0
    };
  }

  /**
   * Get all cached localities
   */
  getCachedLocalities(): LocalityPoint[] {
    const cachedPoints = offlineStorageService.getCachedPoints();
    return cachedPoints.map(point => ({
      ...point,
      expiresAt: this.calculateExpirationDate(point.cachedAt),
    }));
  }

  /**
   * Get cached addresses (read-only list)
   */
  getCachedAddresses(): AddressEntry[] {
    // Delegate to offline storage
    return offlineStorageService.getCachedAddresses() as unknown as AddressEntry[];
  }

  /**
   * Search cached addresses by term (offline)
   */
  searchCachedAddresses(term: string): AddressEntry[] {
    return offlineStorageService.searchCachedAddresses(term) as unknown as AddressEntry[];
  }

  /**
   * Get a specific cached locality by ID
   */
  getLocalityById(id: string): LocalityPoint | null {
    const localities = this.getCachedLocalities();
    const locality = localities.find(l => l.id === id);
    return locality || null;
  }

  /**
   * Add a new locality with cache
   */
  async addLocality(lat: number, lng: number, name: string, address: AddressData | null = null, radius?: number): Promise<LocalityPoint> {
    const settings = this.getSettings();
    const actualRadius = radius || settings.cacheRadius;
    
    // Save the point with the offline storage service
    const newPoint = offlineStorageService.saveCachedPoint(
      lat,
      lng,
      name,
      actualRadius
    );
    
    // If address data was provided, save it directly
    if (address) {
      offlineStorageService.cacheAddress(lat, lng, address);
    }
    // Otherwise try to get address data for this location if online
    else {
      try {
        if (navigator.onLine) {
          await this.preloadAddressForLocation(lat, lng);
        }
      } catch (error) {
        console.warn('Failed to preload address for location:', error);
      }
    }
    
    const localityPoint: LocalityPoint = {
      ...newPoint,
      expiresAt: this.calculateExpirationDate(newPoint.cachedAt),
      address: address || undefined
    };
    
    return localityPoint;
  }

  /**
   * Add current location as a locality
   */
  async addCurrentLocationAsLocality(name?: string): Promise<LocalityPoint> {
    try {
      const position = await geolocationService.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 30000, // 30 seconds timeout
        maximumAge: 0 // Force fresh reading
      });
      
      const pointName = name || `Localidade ${this.getCachedLocalities().length + 1}`;
      return await this.addLocality(position.lat, position.lng, pointName);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(`Erro ao obter localização: ${message}`);
    }
  }

  /**
   * Remove a locality and its cache
   */
  removeLocality(id: string): void {
    offlineStorageService.removeCachedPoint(id);
  }

  /**
   * Clear all cached data
   */
  clearAllCache(): void {
    offlineStorageService.clearAllCache();
  }

  /**
   * Find cached address for a location
   */
  findCachedAddress(lat: number, lng: number): AddressData | null {
    const address = offlineStorageService.findCachedAddress(lat, lng);
    return address ? address.address : null;
  }

  /**
   * Preload address data for a specific location
   */
  async preloadAddressForLocation(lat: number, lng: number): Promise<AddressData | null> {
    if (!navigator.onLine) {
      return null;
    }
    
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1&accept-language=pt-BR,pt,en`
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data && data.address) {
          // Cache the address
          offlineStorageService.cacheAddress(lat, lng, data.address);
          return data.address;
        }
      }
      return null;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      console.error('Error fetching address:', message);
      return null;
    }
  }

  /**
   * Preload addresses for an area
   */
  async preloadAreaAddresses(lat: number, lng: number, radius?: number): Promise<number> {
    const settings = this.getSettings();
    // Prefer the main cache radius in KM unless an explicit radius is provided
    const actualRadiusKm = radius ?? settings.cacheRadius;

    if (navigator.onLine) {
      try {
        // Try Overpass first for richer address coverage
        const overpassCount = await offlineStorageService.preloadAreaAddressesOverpass(
          lat,
          lng,
          actualRadiusKm
        );
        // If Overpass returned results, use them; otherwise fallback
        if (overpassCount > 0) return overpassCount;
      } catch (e) {
        console.warn('Overpass preload failed, falling back to reverse-grid:', e);
      }
    }

    // Fallback to reverse-grid approach (works offline if already cached)
    return await offlineStorageService.preloadAreaAddresses(lat, lng, actualRadiusKm);
  }

  /**
   * Calculate expiration date based on cache timestamp
   */
  private calculateExpirationDate(timestamp: number): number {
    const settings = this.getSettings();
    const expirationMonths = settings.cacheExpiration;
    // Convert months to milliseconds (approximate)
    const expirationMs = expirationMonths * 30 * 24 * 60 * 60 * 1000;
    return timestamp + expirationMs;
  }

  /**
   * Check if a locality is expired
   */
  isLocalityExpired(locality: LocalityPoint): boolean {
    return Date.now() > locality.expiresAt;
  }

  /**
   * Get localities that are about to expire (within next 30 days)
   */
  getExpiringLocalities(): LocalityPoint[] {
    const localities = this.getCachedLocalities();
    const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;
    const now = Date.now();
    
    return localities.filter(locality => {
      const timeUntilExpiration = locality.expiresAt - now;
      return timeUntilExpiration > 0 && timeUntilExpiration < thirtyDaysMs;
    });
  }
  
  /**
   * Get expired localities
   */
  getExpiredLocalities(): LocalityPoint[] {
    const localities = this.getCachedLocalities();
    const now = Date.now();
    
    return localities.filter(locality => locality.expiresAt < now);
  }
  
  /**
   * Clean expired localities from cache
   * @param autoRemove If true, automatically removes expired localities without confirmation
   * @returns Array of removed locality IDs
   */
  cleanExpiredLocalities(autoRemove: boolean = false): string[] {
    const expiredLocalities = this.getExpiredLocalities();
    const removedIds: string[] = [];
    
    if (expiredLocalities.length === 0) {
      return removedIds;
    }
    
    if (autoRemove) {
      // Automatically remove all expired localities
      expiredLocalities.forEach(locality => {
        this.removeLocality(locality.id);
        removedIds.push(locality.id);
      });
    }
    
    return removedIds;
  }
  
  /**
   * Check and clean expired localities on app startup
   * @param autoRemove If true, automatically removes expired localities
   * @returns Object with counts of expired and removed localities
   */
  checkAndCleanExpiredOnStartup(autoRemove: boolean = false): { expired: number, removed: number } {
    const expiredLocalities = this.getExpiredLocalities();
    let removed = 0;
    
    if (autoRemove && expiredLocalities.length > 0) {
      const removedIds = this.cleanExpiredLocalities(true);
      removed = removedIds.length;
    }
    
    return {
      expired: expiredLocalities.length,
      removed
    };
  }
  
  /**
   * Renew locality expiration date
   * @param id ID of the locality to renew
   * @returns Updated locality or null if not found
   */
  renewLocalityExpiration(id: string): LocalityPoint | null {
    const locality = this.getLocalityById(id);
    if (!locality) return null;
    
    // Update the cached point with a new timestamp
    const updatedPoint = offlineStorageService.updateCachedPointTimestamp(id);
    if (!updatedPoint) return null;
    
    return {
      ...updatedPoint,
      expiresAt: this.calculateExpirationDate(updatedPoint.cachedAt),
      address: locality.address
    };
  }

  /**
   * Format date for display
   */
  formatDate(timestamp: number): string {
    return new Date(timestamp).toLocaleDateString('pt-BR');
  }

  /**
   * Format coordinates for display
   */
  formatCoordinates(lat: number, lng: number, precision: number = 6): string {
    return `${lat.toFixed(precision)}, ${lng.toFixed(precision)}`;
  }

  /**
   * Geocode a textual address into coordinates using Nominatim
   * Returns { lat, lng, address? } and caches address when available
   */
  async geocodeAddress(query: string): Promise<{ lat: number; lng: number; address?: AddressData } | null> {
    const q = (query || '').trim();
    if (!q) return null;
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&addressdetails=1&limit=1&accept-language=pt-BR,pt,en`;
      const resp = await fetch(url);
      if (!resp.ok) return null;
      const data = await resp.json();
      if (!Array.isArray(data) || data.length === 0) return null;
      const first = data[0];
      const lat = parseFloat(first.lat);
      const lng = parseFloat(first.lon);
      const address: AddressData | undefined = first.address || undefined;
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      if (address) {
        try {
          offlineStorageService.cacheAddress(lat, lng, address);
        } catch (e) {
          // ignore cache persistence failures
        }
      }
      return { lat, lng, address };
    } catch (error) {
      console.error('Error geocoding address:', error);
      return null;
    }
  }
}

export const mapCacheService = new MapCacheService();
export default mapCacheService;
